import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Input, Space, Timeline } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../store';
import { isModuleApproved } from '../../../lib/utils';
import { useUpdateStatus } from '../../../hooks';
import { PurchaseOrderStatusEnums } from '../../../lib/enums';
import ErrorModal from '../../../components/ui/modal/ErrorModal';
import InfoModal from '../../../components/ui/modal/InfoModal';
import {
  PONextStatus,
  PurchaseOrderInfo,
  PurchaseOrderStatusForm,
} from '../../../types/PurchaseOrder';
import { TimelineItemProps } from 'antd/es/timeline/TimelineItem';
import { StatusHistory } from '../../../types';
import TimelineView from '../../../components/TimelineView';

interface PurchaseOrderInfoStatusProps {
  data?: PurchaseOrderInfo;
  statusHistory: StatusHistory[];
}

const PurchaseOrderInfoStatus: React.FC<PurchaseOrderInfoStatusProps> = ({
  data,
  statusHistory,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { user } = useAuthStore();
  const [cancelClicked, setCancelClicked] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [errorMess, setErrorMess] = useState<string | undefined>('');

  const userModuleAccessRole = user?.moduleAccess?.find(
    (item) => item.module.code === 'PO',
  );

  const {
    mutate,
    isPending,
    isSuccess: isSuccessUpdate,
    isError: isErrorUpdate,
    error: errorUpdate,
  } = useUpdateStatus(`/purchase/orders/${data?.id}/status`);
  const onFinish = (values: any) => {
    const checkAndSetError = (
      field: keyof PurchaseOrderInfo,
      errorMessage: string,
    ) => {
      if (data?.[field] === undefined || data?.[field] === null) {
        setIsErrorModalOpen(true);
        setErrorMess(errorMessage);
        return true; // Indicate that an error occurred
      }
      return false; // Indicate no error
    };

    // check if the required fields have values
    if (
      checkAndSetError('orderDate', 'Please fill up Order date') ||
      checkAndSetError('requiredDate', 'Please fill up required date') ||
      checkAndSetError('vendor', 'Please fill up vendor') ||
      checkAndSetError('destination', 'Please fill up destination')
    ) {
      return; // Stop further processing if any error occurs
    }

    // Proceed with further processing if all required fields have values
    const form: PurchaseOrderStatusForm = {
      status: PONextStatus(data?.status),
      remarks: values.remarks,
    };
    mutate({
      id: data?.id,
      form: form,
    });
  };

  useEffect(() => {
    if (isSuccessUpdate) setIsInfoModalOpen(true);
    if (isErrorUpdate) {
      setIsErrorModalOpen(true);
      setErrorMess(errorUpdate?.response?.data.message);
    }
  }, [isSuccessUpdate, isErrorUpdate]);

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
    navigate('/purchase-order');
  };

  const handleCancelClick = () => {
    const textValue = form.getFieldValue('remarks');
    if (!textValue?.trim()) {
      setCancelClicked(true);
      setErrorMessage('Text is required');
      setTimeout(() => {
        setErrorMessage('');
      }, 2000);
    } else {
      setCancelClicked(false);
      const form: PurchaseOrderStatusForm = {
        status: PurchaseOrderStatusEnums.CANCEL,
        remarks: textValue,
      };
      mutate({
        id: data?.id,
        form: form,
      });
    }
  };

  const renderCancelButton = () => {
    const isAdmin = user?.role === 'ROLE_ADMIN';
    const canCancel =
      userModuleAccessRole?.cancel &&
      (data?.status === PurchaseOrderStatusEnums.DRAFT ||
        data?.status === PurchaseOrderStatusEnums.CONFIRM);

    if (isAdmin || canCancel) {
      return (
        <Button danger onClick={handleCancelClick} loading={isPending}>
          Cancel
        </Button>
      );
    }
    return null;
  };

  let apiTimelineItems: TimelineItemProps[] = [];

  if (statusHistory && statusHistory.length > 0) {
    apiTimelineItems = statusHistory
      .map((item) => ({
        key: item.id,
        children: <TimelineView item={item} />,
      }))
      .sort((a, b) => {
        return parseInt(b.key) - parseInt(a.key);
      });
  }

  const timelineItems: TimelineItemProps[] = [
    {
      key: 'status',
      children: (
        <div className={'space-y-2'}>
          <Form form={form} onFinish={onFinish} layout="vertical">
            <Form.Item
              name="remarks"
              rules={[
                { required: cancelClicked, message: 'Please input your text!' },
              ]}
              validateStatus={cancelClicked ? 'error' : undefined}
              className={cancelClicked ? 'ant-form-item-has-error' : ''}
              help={cancelClicked ? errorMessage : ''}
            >
              <Input.TextArea rows={4} placeholder="Input your text here" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={isPending}>
                  {PONextStatus(data?.status)}
                </Button>
                {renderCancelButton()}
              </Space>
            </Form.Item>
          </Form>
        </div>
      ),
    },
    ...apiTimelineItems,
    {
      key: 'Created',
      children: (
        <div className={'space-y-2 border rounded p-2'}>
          <div>
            <span className={'font-semibold'}>{data?.code}</span> requested by{' '}
            <span className={'font-semibold'}>{data?.createdBy}</span> requested
            date <span className={'font-semibold'}>{data?.timeCreated}</span>.
          </div>
        </div>
      ),
    },
  ];

  const filteredTimelineItems: TimelineItemProps[] = timelineItems.filter(
    (item) => {
      if (item.key !== 'status') {
        return true;
      }

      const recStatus = data?.status;
      const userRole = user?.role;
      const userModuleAccess = user?.moduleAccess;
      // Function to check if the user is an approver or admin
      const isApproverOrAdmin = () => {
        return (
          userRole === 'ROLE_ADMIN' ||
          isModuleApproved(userModuleAccess, 'PurchaseOrder')
        );
      };

      switch (recStatus) {
        case PurchaseOrderStatusEnums.CANCEL:
        case PurchaseOrderStatusEnums.APPROVED:
          return false; // Filter out CANCEL and DONE statuses
        case PurchaseOrderStatusEnums.DRAFT:
          return userModuleAccessRole?.confirm || userRole === 'ROLE_ADMIN';
        case PurchaseOrderStatusEnums.CONFIRM:
          // Allow if user is admin or destination matches default location and user is an approver
          return (
            (isApproverOrAdmin() || userModuleAccessRole?.approve) &&
            user?.username !== data?.createdBy
          );
        default:
          return false; // Filter out any other statuses
      }
    },
  );

  return (
    <>
      <Card style={{ margin: '1rem' }} title={'Status Timeline'}>
        <Timeline reverse={false} items={filteredTimelineItems} />
      </Card>

      <ErrorModal
        visible={isErrorModalOpen}
        message={errorMess}
        onClose={handleErrorModalClose}
      />

      <InfoModal
        visible={isInfoModalOpen}
        message={`Purchase order successfully set to ${PONextStatus(data?.status)}`}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default PurchaseOrderInfoStatus;
